import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from '../shared/AnimatedSection';
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8D48B';
// Step 1 — Portal icon (entry to the realm)
function PortalIcon({
  color
}: {
  color: string;
}) {
  return <svg width="56" height="56" viewBox="0 0 56 56" fill="none" data-id="element-56">
      <defs data-id="element-57">
        <radialGradient id="portal-grad" cx="50%" cy="50%" r="50%" data-id="element-58">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" data-id="element-59" />
          <stop offset="100%" stopColor={color} stopOpacity="0" data-id="element-60" />
        </radialGradient>
      </defs>
      <circle cx="28" cy="28" r="24" fill="url(#portal-grad)" data-id="element-61" />
      <ellipse cx="28" cy="28" rx="18" ry="22" stroke={color} strokeWidth="1" opacity="0.7" data-id="element-62" />
      <ellipse cx="28" cy="28" rx="11" ry="16" stroke={color} strokeWidth="0.8" opacity="0.6" data-id="element-63" />
      <ellipse cx="28" cy="28" rx="5" ry="9" stroke={color} strokeWidth="0.6" opacity="0.5" data-id="element-64" />
      <circle cx="28" cy="28" r="2" fill={color} data-id="element-65" />
      {[0, 60, 120, 180, 240, 300].map(a => <line key={a} x1="28" y1="4" x2="28" y2="7" stroke={color} strokeWidth="1" transform={`rotate(${a} 28 28)`} data-id="element-66" />)}
    </svg>;
}
// Step 2 — Ancient codex / archive
function CodexIcon({
  color
}: {
  color: string;
}) {
  return <svg width="56" height="56" viewBox="0 0 56 56" fill="none" data-id="element-67">
      <circle cx="28" cy="28" r="24" stroke={color} strokeWidth="0.8" opacity="0.4" data-id="element-68" />
      {/* Book covers */}
      <path d="M14 12 L26 14 L26 44 L14 42 Z" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" data-id="element-69" />
      <path d="M42 12 L30 14 L30 44 L42 42 Z" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" data-id="element-70" />
      <line x1="28" y1="14" x2="28" y2="44" stroke={color} strokeWidth="0.8" data-id="element-71" />
      {/* Sacred symbol on cover */}
      <circle cx="28" cy="24" r="3.5" stroke={color} strokeWidth="0.8" fill="none" data-id="element-72" />
      <line x1="28" y1="20.5" x2="28" y2="27.5" stroke={color} strokeWidth="0.6" data-id="element-73" />
      <line x1="24.5" y1="24" x2="31.5" y2="24" stroke={color} strokeWidth="0.6" data-id="element-74" />
      {/* Glowing pages */}
      <line x1="17" y1="20" x2="23" y2="20.7" stroke={color} strokeWidth="0.5" opacity="0.6" data-id="element-75" />
      <line x1="17" y1="24" x2="23" y2="24.7" stroke={color} strokeWidth="0.5" opacity="0.6" data-id="element-76" />
      <line x1="17" y1="28" x2="23" y2="28.7" stroke={color} strokeWidth="0.5" opacity="0.6" data-id="element-77" />
      <line x1="33" y1="20.7" x2="39" y2="20" stroke={color} strokeWidth="0.5" opacity="0.6" data-id="element-78" />
      <line x1="33" y1="24.7" x2="39" y2="24" stroke={color} strokeWidth="0.5" opacity="0.6" data-id="element-79" />
      <line x1="33" y1="28.7" x2="39" y2="28" stroke={color} strokeWidth="0.5" opacity="0.6" data-id="element-80" />
    </svg>;
}
// Step 3 — Astral sigil / sacred symbol
function AstralIcon({
  color
}: {
  color: string;
}) {
  return <svg width="56" height="56" viewBox="0 0 56 56" fill="none" data-id="element-81">
      <circle cx="28" cy="28" r="24" stroke={color} strokeWidth="0.8" opacity="0.5" data-id="element-82" />
      <circle cx="28" cy="28" r="18" stroke={color} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.6" data-id="element-83" />
      {/* Eight-point star */}
      {[0, 45, 90, 135].map(a => <line key={a} x1="28" y1="8" x2="28" y2="48" stroke={color} strokeWidth="0.8" opacity="0.8" transform={`rotate(${a} 28 28)`} data-id="element-84" />)}
      {/* Inner diamond */}
      <path d="M28 16 L36 28 L28 40 L20 28 Z" stroke={color} strokeWidth="0.9" fill={color} fillOpacity="0.12" data-id="element-85" />
      <circle cx="28" cy="28" r="3" fill={color} data-id="element-86" />
      {/* Orbiting nodes */}
      {[0, 90, 180, 270].map(a => <circle key={a} cx="28" cy="6" r="1.5" fill={color} transform={`rotate(${a} 28 28)`} data-id="element-87" />)}
    </svg>;
}
export function ComoFunciona() {
  const steps = [{
    title: 'Selecciona tu Reino',
    description: 'Cada entidad representa patrones emocionales, impulsos internos y una energía arquetípica distinta.',
    Icon: PortalIcon
  }, {
    title: 'Accede al Núcleo del Arquetipo',
    description: 'Desbloquea una experiencia digital diseñada para explorar la identidad, emociones y comportamientos que definen tu energía.',
    Icon: CodexIcon
  }, {
    title: 'Explora Tu Energía',
    description: 'Descubre patrones internos, impulsos emocionales, relaciones, sombras y símbolos ligados a tu arquetipo.',
    Icon: AstralIcon
  }];
  return <section className="relative py-24 px-6 bg-cosmic-black overflow-hidden pt-[20px] pb-[20px]" data-id="element-88">
      {/* Ambient atmospheric particles */}
      <div className="absolute inset-0 pointer-events-none opacity-40" data-id="element-89">
        {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
        backgroundColor: GOLD,
        left: `${i * 8.3 % 100}%`,
        top: `${i * 13 % 100}%`,
        boxShadow: `0 0 6px ${GOLD}`
      }} animate={{
        y: [-10, -40, -10],
        opacity: [0.2, 0.6, 0.2]
      }} transition={{
        duration: 6 + i % 4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.3
      }} data-id="element-90" />)}
      </div>

      <div className="relative container mx-auto max-w-6xl" data-id="element-91">
        <AnimatedSection data-id="element-92">
          <div className="text-center mb-4" data-id="element-93">
            <motion.div className="inline-flex items-center gap-3 mb-6" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-94">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold-primary" data-id="element-95" />
              <span className="tracking-[0.4em] uppercase font-serif text-gold-light text-[14px] mt-[0px] mb-[0px]" data-id="element-96">
                La Travesía
              </span>
              <div className="h-px w-12 bg-gradient-to-r from-gold-primary to-transparent" data-id="element-97" />
            </motion.div>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-center text-white mb-20" data-id="element-98">
            Cómo Funciona
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10" data-id="element-99">
          {steps.map((step, index) => {
          const {
            Icon
          } = step;
          return <AnimatedSection key={index} delay={0.2 + index * 0.15} data-id="element-100">
                <motion.div className="group relative h-full rounded-2xl p-8 md:p-10 text-center overflow-hidden" whileHover={{
              y: -4
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 22
            }} style={{
              background: 'linear-gradient(160deg, rgba(14,14,22,0.9) 0%, rgba(201,168,76,0.06) 100%)',
              border: '1px solid rgba(201,168,76,0.25)',
              boxShadow: '0 0 30px rgba(201,168,76,0.08), inset 0 0 0 1px rgba(201,168,76,0.05)'
            }} data-id="element-101">
                  {/* Hover glow */}
                  <motion.div className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                boxShadow: `inset 0 0 60px ${GOLD}25, 0 0 40px ${GOLD}40`,
                border: `1px solid ${GOLD}`
              }} data-id="element-102" />

                  {/* Step number — cinematic Roman numeral */}
                  <p className="text-[11px] tracking-[0.5em] uppercase font-serif mb-6 opacity-60" style={{
                color: GOLD_LIGHT
              }} data-id="element-103">
                    {['I', 'II', 'III'][index]}
                  </p>

                  {/* Icon */}
                  <div className="relative mb-8 flex justify-center" data-id="element-104">
                    <motion.div className="absolute" animate={{
                  scale: [1, 1.15, 1],
                  opacity: [0.4, 0.7, 0.4]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: index * 0.5
                }} style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  boxShadow: `0 0 40px ${GOLD}60`
                }} data-id="element-105" />
                    <motion.div className="relative" animate={{
                  rotate: 360
                }} transition={{
                  duration: 80,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-106">
                      <Icon color={GOLD} data-id="element-107" />
                    </motion.div>
                  </div>

                  {/* Divider */}
                  <div className="relative flex justify-center mb-5" data-id="element-108">
                    <div className="h-px w-16" style={{
                  background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`
                }} data-id="element-109" />
                  </div>

                  <h3 className="text-xl md:text-2xl font-serif font-semibold text-white mb-4" style={{
                textShadow: `0 0 20px ${GOLD}30`
              }} data-id="element-110">
                    {step.title}
                  </h3>
                  <p className="text-gray-300/90 leading-relaxed text-[15px]" data-id="element-111">
                    {step.description}
                  </p>
                </motion.div>
              </AnimatedSection>;
        })}
        </div>
      </div>
    </section>;
}